import { useId } from 'react';
import { Alert, Flex, Link, Skeleton, Text } from '@backstage/ui';
import { useRouteRef } from '@backstage/core-plugin-api';
import { stringifyEntityRef } from '@backstage/catalog-model';
import type { Entity } from '@backstage/catalog-model';
import { buildTechDocsURL } from '@backstage/plugin-techdocs-react';
import { DOC_TYPE_ANNOTATION } from '@internal/plugin-neo4j-techdocs-common';
import { RiFileTextLine } from '@remixicon/react';
import { Timestamp } from '@internal/backstage-plugin-neo4j-ui-kit-react';
import { DOC_TYPES, RECENT_DOCS_TITLE } from '../types';
import type { DocTypeData } from '../types';
import { DocsSection } from './DocsSection';
import { createTechdocsReaderPageRef } from '../routes';
import { useDocEntities } from '../hooks/useDocEntities';
import { useUserRecentReadComponents } from '../hooks/useUserRecentReadComponents';
import type { RecentRead } from '../hooks/useUserRecentReadComponents';
import styles from './RecentDocs.module.css';

const LIST_SKELETON_ROW_COUNT = 3;

export function RecentDocs({
  docType,
  emptyText,
}: {
  docType?: string;
  emptyText: string;
}) {
  const headingId = useId();
  const readEvents = useUserRecentReadComponents();
  const lastReadTimes = getLastReadTimes(readEvents.value);
  const entityRefs = [...lastReadTimes.keys()];
  const { data, isPending, error } = useDocEntities(entityRefs);
  const entities = docType
    ? data?.filter(entity => getDocType(entity)?.type === docType)
    : data;

  return (
    <DocsSection title={RECENT_DOCS_TITLE} headingId={headingId}>
      <RecentDocsContent
        headingId={headingId}
        entities={entities}
        lastReadTimes={lastReadTimes}
        isPending={readEvents.loading || isPending}
        error={readEvents.error ?? error}
        emptyText={emptyText}
      />
    </DocsSection>
  );
}

function RecentDocsContent({
  headingId,
  entities,
  lastReadTimes,
  isPending,
  error,
  emptyText,
}: {
  headingId: string;
  entities: Entity[] | undefined;
  lastReadTimes: Map<string, number | undefined>;
  isPending: boolean;
  error: Error | null | undefined;
  emptyText: string;
}) {
  const getReaderPath = useRouteRef(createTechdocsReaderPageRef);

  if (error) {
    return (
      <Alert
        status="danger"
        icon
        title="Could not load your recently viewed docs"
        description={error.message}
      />
    );
  }

  if (isPending || !entities) {
    return (
      <Flex direction="column" gap="2" className={styles.docList}>
        {Array.from({ length: LIST_SKELETON_ROW_COUNT }, (_, index) => (
          <Skeleton key={index} width="80%" height={32} />
        ))}
      </Flex>
    );
  }

  if (entities.length === 0) {
    return (
      <Text variant="body-medium" color="secondary">
        {emptyText}
      </Text>
    );
  }

  // Timestamp's tooltip trigger is a button, so it must stay outside the doc link.
  return (
    <ul aria-labelledby={headingId} className={styles.docList}>
      {entities.map(entity => {
        const entityRef = stringifyEntityRef(entity);
        const Icon = getDocType(entity)?.icon ?? RiFileTextLine;
        const timestamp = lastReadTimes.get(entityRef);

        return (
          <li key={entityRef} className={styles.docRow}>
            <span className={styles.docIcon}>
              <Icon size={16} aria-hidden="true" />
            </span>
            <Link
              href={buildTechDocsURL(entity, getReaderPath) ?? ''}
              variant="body-medium"
              truncate
              className={styles.docLink}
            >
              {entity.metadata.title ?? entity.metadata.name}
            </Link>
            {timestamp !== undefined && (
              <Text
                as="span"
                variant="body-small"
                color="secondary"
                className={styles.docTime}
              >
                <Timestamp
                  timestamp={timestamp}
                  displayTimezone="local"
                  tooltipPlacement="top"
                />
              </Text>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * Reads arrive newest first. Keep the first event per doc to retain its latest
 * timestamp and list position.
 */
function getLastReadTimes(reads: RecentRead[]) {
  const times = new Map<string, number | undefined>();
  for (const { entityRef, timestamp } of reads) {
    const ref = stringifyEntityRef(entityRef);
    if (!times.has(ref)) {
      times.set(ref, timestamp);
    }
  }
  return times;
}

function getDocType(entity: Entity): DocTypeData | undefined {
  const docType = entity.metadata.annotations?.[DOC_TYPE_ANNOTATION];
  return docType ? DOC_TYPES[docType] : undefined;
}
